import React, { useState, useEffect } from 'react';
import { Package, Plus, Trash2, ChevronDown, ChevronUp, Settings, X, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { toast } from 'sonner';

const CustomVolumeCalculator = ({ 
  volumes, 
  setVolumes, 
  materials, 
  title = "Custom Volume Calculator",
  factorLabel = "Compaction Factor",
  factorDescription = "1.0 = No compaction, 1.2 = 20% more material needed for compaction",
  storageKey = "customVolumeMaterials"
}) => {
  const [showForm, setShowForm] = useState(false);
  const [showCustomManager, setShowCustomManager] = useState(false);
  const [currentVolume, setCurrentVolume] = useState({
    name: '',
    length: '',
    width: '',
    height: '',
    material: null,
    compactionFactor: '1.0',
    costPerTon: '0'
  });

  // Custom materials state
  const [customMaterials, setCustomMaterials] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? JSON.parse(saved) : [];
  });

  // New custom material form
  const [newCustomMaterial, setNewCustomMaterial] = useState({
    name: '',
    tonsPerCubicYard: '',
    category: 'Custom'
  });

  // Save custom materials to localStorage
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(customMaterials));
  }, [customMaterials, storageKey]);

  // Combined materials list (default + custom)
  const allMaterials = [...materials, ...customMaterials];

  const addCustomMaterial = () => {
    if (!newCustomMaterial.name.trim()) {
      toast.error('Please enter a material name');
      return;
    }
    if (!newCustomMaterial.tonsPerCubicYard || parseFloat(newCustomMaterial.tonsPerCubicYard) <= 0) {
      toast.error('Please enter a valid tons per cubic yard');
      return;
    }

    const newMat = {
      id: Date.now(),
      name: newCustomMaterial.name,
      tonsPerCubicYard: parseFloat(newCustomMaterial.tonsPerCubicYard),
      category: 'Custom',
      isCustom: true
    };

    setCustomMaterials([...customMaterials, newMat]);
    setNewCustomMaterial({ name: '', tonsPerCubicYard: '', category: 'Custom' });
    toast.success(`Custom material "${newMat.name}" added`);
  };

  const deleteCustomMaterial = (id) => {
    setCustomMaterials(customMaterials.filter(m => m.id !== id));
    toast.success('Custom material removed');
  };

  const handleInputChange = (field, value) => {
    setCurrentVolume(prev => ({ ...prev, [field]: value }));
  };

  const handleMaterialSelect = (materialName) => {
    const material = allMaterials.find(m => m.name === materialName);
    setCurrentVolume(prev => ({ ...prev, material }));
  };

  const calculateVolume = (volume) => {
    const length = parseFloat(volume.length) || 0;
    const width = parseFloat(volume.width) || 0;
    const height = parseFloat(volume.height) || 0;
    const compaction = parseFloat(volume.compactionFactor) || 1.0;
    
    // Calculate cubic yards (length × width × height / 27)
    const cubicYards = (length * width * height) / 27;
    const adjustedCubicYards = cubicYards * compaction;
    
    // Calculate tons using material's tons per cubic yard
    const tonsPerCY = volume.material?.tonsPerCubicYard || 0;
    const totalTons = adjustedCubicYards * tonsPerCY;
    const totalPounds = totalTons * 2000;
    
    return {
      cubicYards: adjustedCubicYards,
      totalTons,
      totalPounds
    };
  };

  const handleAddVolume = () => {
    if (!currentVolume.name.trim()) {
      alert('Please enter a name');
      return;
    }

    if (!currentVolume.material) {
      alert('Please select a material');
      return;
    }

    if (!currentVolume.length || !currentVolume.width || !currentVolume.height) {
      alert('Please enter length, width, and height');
      return;
    }

    const calculation = calculateVolume(currentVolume);
    
    if (calculation.totalTons === 0) {
      alert('Calculation resulted in 0 tons. Please check your inputs.');
      return;
    }

    const costPerTon = parseFloat(currentVolume.costPerTon) || 0;
    const totalCost = calculation.totalTons * costPerTon;

    const newVolume = {
      id: Date.now(),
      ...currentVolume,
      cubicYards: calculation.cubicYards,
      totalTons: calculation.totalTons,
      totalPounds: calculation.totalPounds,
      costPerTon: costPerTon,
      totalCost: totalCost,
      addedAt: new Date().toISOString()
    };

    setVolumes([...volumes, newVolume]);
    
    // Reset form
    setCurrentVolume({
      name: '',
      length: '',
      width: '',
      height: '',
      material: null,
      compactionFactor: '1.0',
      costPerTon: '0'
    });
    setShowForm(false);
  };

  const handleDeleteVolume = (id) => {
    setVolumes(volumes.filter(v => v.id !== id));
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(num);
  };

  const getTotals = () => {
    const totalCubicYards = volumes.reduce((sum, v) => sum + (v.cubicYards || 0), 0);
    const totalTons = volumes.reduce((sum, v) => sum + (v.totalTons || 0), 0);
    const totalPounds = totalTons * 2000;
    const totalCost = volumes.reduce((sum, v) => sum + (v.totalCost || 0), 0);
    const itemCount = volumes.length;
    
    return { itemCount, totalCubicYards, totalTons, totalPounds, totalCost };
  };

  const totals = getTotals();

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            {title}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p className="text-sm">Calculate volume-based material costs by entering dimensions (L×W×H). Select a material type and add a compaction factor if needed. The calculator converts cubic yards to tons based on material density.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Dialog open={showCustomManager} onOpenChange={setShowCustomManager}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Manage Custom
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Manage Custom Materials</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-2">
                  <Input 
                    placeholder="Material Name" 
                    value={newCustomMaterial.name} 
                    onChange={(e) => setNewCustomMaterial({...newCustomMaterial, name: e.target.value})} 
                  />
                  <Input 
                    type="number" 
                    step="0.01"
                    placeholder="Tons/CY" 
                    value={newCustomMaterial.tonsPerCubicYard} 
                    onChange={(e) => setNewCustomMaterial({...newCustomMaterial, tonsPerCubicYard: e.target.value})} 
                  />
                  <Button type="button" onClick={addCustomMaterial}>
                    <Plus className="w-4 h-4 mr-1" />Add
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Add custom materials with their weight in tons per cubic yard.
                </p>
                {customMaterials.length > 0 && (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    <Label className="text-sm font-medium">Custom Materials</Label>
                    {customMaterials.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                        <span className="text-sm">{item.name} - {item.tonsPerCubicYard} tons/CY</span>
                        <Button variant="ghost" size="sm" onClick={() => deleteCustomMaterial(item.id)}>
                          <X className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {customMaterials.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No custom materials added yet.
                  </p>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-muted rounded-lg">
          <div>
            <p className="text-sm text-muted-foreground">Items</p>
            <p className="text-2xl font-bold">{totals.itemCount}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Cubic Yards</p>
            <p className="text-2xl font-bold">{formatNumber(totals.totalCubicYards)}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Weight</p>
            <p className="text-2xl font-bold">{formatNumber(totals.totalTons)} tons</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Pounds</p>
            <p className="text-2xl font-bold">{formatNumber(totals.totalPounds)} lbs</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Cost</p>
            <p className="text-2xl font-bold text-success">${formatNumber(totals.totalCost)}</p>
          </div>
        </div>

        {/* Add Button */}
        <Button
          onClick={() => setShowForm(!showForm)}
          className="w-full"
          variant={showForm ? "outline" : "default"}
        >
          {showForm ? (
            <>
              <ChevronUp className="w-4 h-4 mr-2" />
              Hide Form
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              Add Custom Volume
            </>
          )}
        </Button>

        {/* Add Form */}
        {showForm && (
          <div className="space-y-4 p-4 border rounded-lg bg-card">
            <div>
              <Label htmlFor="volumeName">Name/Description *</Label>
              <Input
                id="volumeName"
                placeholder="e.g., Parking Lot Fill, Foundation Excavation"
                value={currentVolume.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Length (ft)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={currentVolume.length}
                  onChange={(e) => handleInputChange('length', e.target.value)}
                />
              </div>
              <div>
                <Label>Width (ft)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={currentVolume.width}
                  onChange={(e) => handleInputChange('width', e.target.value)}
                />
              </div>
              <div>
                <Label>Height/Depth (ft)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={currentVolume.height}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                />
              </div>
            </div>

            {currentVolume.length && currentVolume.width && currentVolume.height && (
              <p className="text-sm text-muted-foreground">
                = {formatNumber(parseFloat(currentVolume.length) * parseFloat(currentVolume.width) * parseFloat(currentVolume.height) / 27)} cubic yards
              </p>
            )}

            <div>
              <Label>Material *</Label>
              <Select onValueChange={handleMaterialSelect} value={currentVolume.material?.name}>
                <SelectTrigger>
                  <SelectValue placeholder="Select material" />
                </SelectTrigger>
                <SelectContent>
                  {allMaterials.map((mat) => (
                    <SelectItem key={mat.name} value={mat.name}>
                      {mat.name} ({mat.tonsPerCubicYard} tons/cy) {mat.isCustom && '⭐'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{factorLabel}</Label>
              <Input
                type="number"
                step="0.1"
                placeholder="1.0"
                value={currentVolume.compactionFactor}
                onChange={(e) => handleInputChange('compactionFactor', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {factorDescription}
              </p>
            </div>

            <div>
              <Label>Cost per Ton (Optional)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={currentVolume.costPerTon}
                onChange={(e) => handleInputChange('costPerTon', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter cost per ton to calculate total material cost
              </p>
            </div>

            <Button onClick={handleAddVolume} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Volume
            </Button>
          </div>
        )}

        {/* Volumes List */}
        {volumes.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-semibold">Saved Volumes</h4>
            {volumes.map((volume) => (
              <Card key={volume.id} className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h5 className="font-semibold">{volume.name}</h5>
                    <p className="text-sm text-muted-foreground">
                      {volume.material?.name}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteVolume(volume.id)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Dimensions:</span>
                    <p>{volume.length}&apos; × {volume.width}&apos; × {volume.height}&apos;</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Volume:</span>
                    <p>{formatNumber(volume.cubicYards)} CY</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{factorLabel.split(' ')[0]}:</span>
                    <p>{volume.compactionFactor}x</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Weight:</span>
                    <p className="font-semibold text-accent">{formatNumber(volume.totalTons)} tons</p>
                    <p className="text-xs text-muted-foreground">{formatNumber(volume.totalPounds)} lbs</p>
                  </div>
                  {volume.costPerTon > 0 && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Cost/Ton:</span>
                        <p>${formatNumber(volume.costPerTon)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Cost:</span>
                        <p className="font-semibold text-success">${formatNumber(volume.totalCost)}</p>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {volumes.length === 0 && !showForm && (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No custom volumes added yet</p>
            <p className="text-sm">Click &quot;Add Custom Volume&quot; to get started</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomVolumeCalculator;
